// Angle Types and Basics lesson — Grade 8/9 Math
// Schema mirrors photosynthesisLesson so it plugs into LessonStage + TeacherPanel unchanged.

export const angleLesson = {
    sessionName: "Angle Types and Basics",
    title: "Angle Types and Basics",
    subtitle: "Understanding acute, right, obtuse, straight angles, and angle sum rules",
    grade: "Grade 8–9 · Math",
    chapter: "Geometry · Angle Basics",
    subject: "Math",
    coverImage: "https://asset.sparkl.me/pb/poc-folder/ZHH26NsPfyvHfYCl6i6hjGPPvWzNdsBQac4O5XEr.png",
    coverAlt: "Drafting compass and geometry tools on a blueprint",

    /* ---- physical slides ---- */
    slides: [
        // Slide 1: Angle Types and Basics
        {
            id: "angle-types-basics",
            section: "concept",
            mode: "diagram_teaching",
            title: "Angle Types and Basics",
            estimatedSeconds: 90,
            tags: ["definition", "basics"],
            fragments: [
                {
                    caption: "Intro to angle types.",
                },
                {
                    caption: "An angle is formed by two rays with a common vertex. Its size in degrees measures the turn between them.",
                },
                {
                    bullet: "Acute angle",
                    caption: "Acute angle: Greater than 0° and less than 90°.",
                },
                {
                    bullet: "Right angle",
                    caption: "Right angle: Exactly 90° (often marked with a square).",
                },
                {
                    bullet: "Obtuse angle",
                    caption: "Obtuse angle: Greater than 90° and less than 180°.",
                },
                {
                    bullet: "Straight angle",
                    caption: "Straight angle: Exactly 180° (forms a flat horizontal line).",
                },
                {
                    bullet: "SAT Tip",
                    caption: "SAT Tip: Boundary values matter: 90° is right (not acute), and 180° is straight. Always use the numeric measure over visual cues.",
                }
            ]
        },

        // Slide 2: Core Angle Measure Facts
        {
            id: "angle-measure-facts",
            section: "concept",
            mode: "diagram_teaching",
            title: "Core Angle Measure Facts",
            estimatedSeconds: 90,
            tags: ["rules", "180", "360"],
            fragments: [
                {
                    caption: "Core rules intro.",
                },
                {
                    bullet: "180° Rule",
                    caption: "180° Rule: Angles that lie along a straight line sum to 180°. If a point on a line is split into two non-overlapping angles, their measures add to 180°.",
                },
                {
                    bullet: "Example (180°)",
                    caption: "Example: On a straight line, one angle is 65°. The adjacent angle is 180° - 65° = 115°.",
                },
                {
                    bullet: "360° Rule",
                    caption: "360° Rule: Angles that completely wrap around a point sum to 360°. Any set of non-overlapping angles filling a full turn add to 360°.",
                },
                {
                    bullet: "Example (360°)",
                    caption: "Example: Three angles around a point are 120°, 95°, and x°. x = 360° - (120° + 95°) = 145°.",
                }
            ]
        },

        // Slide 3: Algebra in Angle Geometry
        {
            id: "angle-algebra",
            section: "concept",
            mode: "diagram_teaching",
            title: "Algebra in Angle Geometry",
            estimatedSeconds: 100,
            tags: ["algebra", "solving"],
            fragments: [
                {
                    caption: "Algebra application intro.",
                },
                {
                    bullet: "Strategy",
                    caption: "Strategy: Unknown angle measures are often expressed with variables. Translate the diagram into an equation using 180° (straight line).",
                },
                {
                    bullet: "Diagram",
                    caption: "Let's look at this diagram where a straight line is split into two angles, one labeled 2x + 10 degrees and the other 4x + 20 degrees.",
                },
                {
                    bullet: "Step 1: Setup",
                    caption: "Step 1: Setup. Since they lie on a straight line, we set their sum to 180 degrees. So, (2x + 10) + (4x + 20) = 180.",
                },
                {
                    bullet: "Step 2: Solve",
                    caption: "Step 2: Solve. Combine like terms to get 6x + 30 = 180, which simplifies to 6x = 150. Solving for x, we get x = 25.",
                },
                {
                    bullet: "Step 3: Substitute & Check",
                    caption: "Step 3: Substitute & Check. Plugging in 25 for x gives 2(25) + 10 = 60 degrees, and 4(25) + 20 = 120 degrees. Since 60 + 120 = 180, it works!",
                }
            ]
        },

        // CONTAINER SLIDES ----------------------------------------------------
        {
            id: "mcq-display",
            section: "concept-check",
            mode: "mcq_display",
            title: "Concept Check",
            estimatedSeconds: 60,
            tags: ["assessment"],
            notes: ["Wait at least 30s before revealing."],
            misconceptions: [],
            fragments: [{ caption: "Read the question carefully." }],
        },
        {
            id: "subjective-display",
            section: "practice",
            mode: "subjective_display",
            title: "Practice / Solved Example",
            estimatedSeconds: 120,
            tags: ["subjective"],
            notes: ["Walk the room as students think."],
            misconceptions: [],
            fragments: [{ caption: "Take a moment to think this through." }],
        },
        {
            id: "drag-drop",
            section: "activity",
            mode: "drag_drop",
            title: "Sort Angle Sizes: Drag-and-drop",
            estimatedSeconds: 180,
            tags: ["activity"],
            notes: ["Help students recognize boundary values."],
            misconceptions: [],
            fragments: [{ caption: "Sort each angle size into Acute or Obtuse." }],
        },
        {
            id: "blank-slide",
            section: "tools",
            mode: "blank_slide",
            title: "Blank teaching slide",
            estimatedSeconds: 0,
            tags: ["blank"],
            fragments: [{ caption: "Free-form board for live explanation." }],
        },
        {
            id: "real-life-example",
            section: "tools",
            mode: "real_life_example",
            title: "Real-life example",
            estimatedSeconds: 30,
            tags: ["analogy"],
            notes: ["Connect the geometric definitions to physical applications."],
            misconceptions: [],
            fragments: [{ caption: "" }],
        },
        {
            id: "angle-recap",
            section: "recap",
            mode: "recap",
            title: "Recap",
            estimatedSeconds: 60,
            tags: ["recap"],
            fragments: [
                { caption: "An angle is formed by two rays with a common vertex. Its size in degrees measures the turn between them." },
                { caption: "Remember the four basic types: Acute is less than 90°, Right is exactly 90°, Obtuse is between 90° and 180°, and Straight is exactly 180°." },
                { caption: "We also have the 180° Rule for angles on a straight line, and the 360° Rule for angles that completely wrap around a point." },
                { caption: "And finally, we can use these rules to set up algebraic equations and solve for unknown variables in diagrams." },
            ],
        },
    ],

    /* ---- Sections config ---- */
    sections: [
        {
            id: "concept",
            title: "Concept",
            description: "Auto-flows unless paused",
            slideIds: [
                "angle-types-basics",
                "angle-measure-facts",
                "angle-algebra",
            ],
        },
        { id: "concept-check", title: "Concept Check", description: "5 MCQs" },
        {
            id: "practice",
            title: "Practice",
            description: "3 subjective questions",
        },
        {
            id: "activity",
            title: "Activity",
            description: "Drag & Drop",
            slideIds: ["drag-drop"],
        },
        {
            id: "recap",
            title: "Recap",
            description: "Key takeaways",
            slideIds: ["angle-recap"],
        },
    ],

    /* ---- 5 concept-check MCQs ---- */
    mcqs: [
        {
            id: "m1",
            concept: "Angle classifications",
            question: "An angle measuring exactly 90° is classified as:",
            q: "An angle measuring exactly 90° is classified as:",
            options: ["Acute", "Right", "Obtuse", "Straight"],
            correctIndex: 1,
            correct_option_id: "B",
            difficulty: "easy",
            tag: "basics",
            estimatedSeconds: 30,
            relatedConceptId: "angle-types-basics",
            explanation: "Exactly 90° is a right angle, often marked with a square.",
            explain: "Exactly 90° is a right angle, often marked with a square."
        },
        {
            id: "m2",
            concept: "180 degree rule",
            question: "If two angles lie along a straight line, what must they sum to?",
            q: "If two angles lie along a straight line, what must they sum to?",
            options: ["90°", "180°", "270°", "360°"],
            correctIndex: 1,
            correct_option_id: "B",
            difficulty: "easy",
            tag: "rules",
            estimatedSeconds: 30,
            relatedConceptId: "angle-measure-facts",
            explanation: "Angles that lie along a straight line always sum to 180° (180° Rule).",
            explain: "Angles that lie along a straight line always sum to 180° (180° Rule)."
        },
        {
            id: "m3",
            concept: "360 degree rule",
            question: "Three angles completely wrap around a single point. If two of them are 120° and 100°, what is the measure of the third angle?",
            q: "Three angles completely wrap around a single point. If two of them are 120° and 100°, what is the measure of the third angle?",
            options: ["60°", "140°", "180°", "260°"],
            correctIndex: 1,
            correct_option_id: "B",
            difficulty: "medium",
            tag: "rules",
            estimatedSeconds: 40,
            relatedConceptId: "angle-measure-facts",
            explanation: "Angles around a point sum to 360°. So, 360° - (120° + 100°) = 140°.",
            explain: "Angles around a point sum to 360°. So, 360° - (120° + 100°) = 140°."
        },
        {
            id: "m4",
            concept: "Decimal angle bounds",
            question: "An angle measures 89.9°. How is it classified?",
            q: "An angle measures 89.9°. How is it classified?",
            options: ["Acute", "Right", "Obtuse", "Straight"],
            correctIndex: 0,
            correct_option_id: "A",
            difficulty: "easy",
            tag: "basics",
            estimatedSeconds: 25,
            relatedConceptId: "angle-types-basics",
            explanation: "An angle less than 90° (even 89.9°) is acute. 90° is exactly a right angle.",
            explain: "An angle less than 90° (even 89.9°) is acute. 90° is exactly a right angle."
        },
        {
            id: "m5",
            concept: "Algebraic angle sums",
            question: "Two angles on a straight line are represented as (3x)° and (x)°. What is the value of x?",
            q: "Two angles on a straight line are represented as (3x)° and (x)°. What is the value of x?",
            options: ["30", "45", "60", "90"],
            correctIndex: 1,
            correct_option_id: "B",
            difficulty: "medium",
            tag: "algebra",
            estimatedSeconds: 30,
            relatedConceptId: "angle-algebra",
            explanation: "Angles on a line sum to 180°. So 3x + x = 180 => 4x = 180 => x = 45.",
            explain: "Angles on a line sum to 180°. So 3x + x = 180 => 4x = 180 => x = 45."
        },
    ],

    /* ---- 3 subjective questions ---- */
    subjectives: [
        {
            id: "s1",
            question: "On a straight line, one angle is 72°. Find the measure of its adjacent angle.",
            difficulty: "easy",
            tag: "rules",
            estimatedSeconds: 120,
            relatedConceptId: "angle-measure-facts",
            hint: "Use the 180° Rule for angles on a straight line.",
            expectedPoints: [
                "Set up equation: x + 72 = 180",
                "Subtract 72 from 180: x = 108°",
                "Check: 72 + 108 = 180°"
            ],
            commonMistake: "Subtracting from 90 instead of 180.",
            steps: [
                {
                    title: "Identify straight line rule",
                    body: "Adjacent angles on a line sum to 180°."
                },
                {
                    title: "Set up the equation",
                    body: "Adjacent angle = 180° - 72°"
                },
                {
                    title: "Solve",
                    body: "Adjacent angle = 108°"
                },
            ],
        },
        {
            id: "s2",
            question: "Four angles around a point are x°, 2x°, 30°, and 150°. Solve for x.",
            difficulty: "medium",
            tag: "rules",
            estimatedSeconds: 150,
            relatedConceptId: "angle-measure-facts",
            hint: "Angles around a point completely wrap around to sum to 360°.",
            expectedPoints: [
                "Set up equation: x + 2x + 30 + 150 = 360",
                "Simplify: 3x + 180 = 360",
                "Solve: 3x = 180 => x = 60"
            ],
            commonMistake: "Setting the sum to 180 instead of 360.",
            steps: [
                {
                    title: "Set up the sum",
                    body: "x + 2x + 30 + 150 = 360"
                },
                {
                    title: "Combine like terms",
                    body: "3x + 180 = 360"
                },
                {
                    title: "Solve for x",
                    body: "3x = 180 => x = 60"
                },
            ],
        },
        {
            id: "s3",
            question: "Find the value of y if two angles on a straight line are (5y - 10)° and (3y + 30)°.",
            difficulty: "hard",
            tag: "algebra",
            estimatedSeconds: 180,
            relatedConceptId: "angle-algebra",
            hint: "Set their sum to 180° and solve for y.",
            expectedPoints: [
                "Set up equation: (5y - 10) + (3y + 30) = 180",
                "Combine terms: 8y + 20 = 180",
                "Solve: 8y = 160 => y = 20"
            ],
            commonMistake: "Multiplying terms instead of adding them.",
            steps: [
                {
                    title: "Add the two expressions",
                    body: "(5y - 10) + (3y + 30) = 180"
                },
                {
                    title: "Combine like terms",
                    body: "8y + 20 = 180"
                },
                {
                    title: "Solve",
                    body: "8y = 160 => y = 20"
                },
            ],
        },
    ],

    /* ---- Drag-drop activity (single object, matches photosynthesis schema) ---- */
    activity: {
        id: "drag-drop-1",
        title: "Sort Angle Sizes",
        objective: "Sort each angle size by whether it is acute or obtuse.",
        instruction: "Drag each angle into the correct bucket — is it Acute (< 90°) or Obtuse (> 90°)?",
        difficulty: "easy",
        duration: 180,
        items: [
            { id: "i1", label: "45°", correct: "acute" },
            { id: "i2", label: "120°", correct: "obtuse" },
            { id: "i3", label: "15°", correct: "acute" },
            { id: "i4", label: "165°", correct: "obtuse" },
            { id: "i5", label: "85°", correct: "acute" },
            { id: "i6", label: "95°", correct: "obtuse" },
            { id: "i7", label: "60°", correct: "acute" },
            { id: "i8", label: "135°", correct: "obtuse" },
            // distractors
            { id: "i9", label: "90°", correct: null },
            { id: "i10", label: "180°", correct: null },
        ],
        categories: [
            { id: "acute", label: "Acute Angle" },
            { id: "obtuse", label: "Obtuse Angle" },
        ],
    },

    /* ---- Blank slate variants ---- */
    blankSlideTypes: [
        { id: "white", label: "White board" },
        { id: "lined", label: "Lined" },
        { id: "grid", label: "Grid" },
        { id: "diagram", label: "Diagram" },
    ],

    /* ---- real-life examples ---- */
    realLifeExamples: [
        {
            id: "e1",
            text: "A partially opened pair of scissors forms an acute angle.",
            headline: "Scissor Blades",
            description: "When you open scissors to cut paper, the blades form an acute angle at the pivot point. Adjusting the opening changes the angle size.\n\n💡 Teaching Point: Angles are formed by two intersecting lines. A typical scissor opening is an acute angle (less than 90°).",
            image: "https://asset.sparkl.me/pb/poc-folder/ZHH26NsPfyvHfYCl6i6hjGPPvWzNdsBQac4O5XEr.png",
        },
        {
            id: "e2",
            text: "The hands of a clock form different angles throughout the day.",
            headline: "Clock Hands",
            description: "At exactly 3:00, the minute and hour hands form a perfect 90° right angle. At 6:00, they lie on a straight line forming a 180° straight angle.\n\n💡 Teaching Point: The hands of a clock show angle rotation. As time ticks, the angle between the hands changes through acute, right, obtuse, and straight configurations.",
            image: "https://images.unsplash.com/photo-1563861826100-9cb868fdbe1c?auto=format&fit=crop&w=1200&q=80",
        },
        {
            id: "e3",
            text: "Roof trusses use triangles and angles for structural support.",
            headline: "Roof Trusses",
            description: "Houses are built with triangular roof structures (trusses). The peak of the roof forms an obtuse angle, while the base corners form acute angles to distribute weight evenly.\n\n💡 Teaching Point: Triangles contain angles that must sum to 180°. Strong structural engineering relies on precise angle calculations to distribute forces.",
            image: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=1200&q=80",
        },
    ],

    /* ---- teacher-help library ---- */
    teacherHelp: {
        "angle-types-basics": {
            simple: "Angles are classified based on their measures relative to 90° and 180°.",
            probing: "How would you describe the difference between a right angle and a straight angle?",
            misconception: "Confusing acute with obtuse, or thinking 90° can be acute.",
            analogy: "Like a door opening — a crack is acute, standard open is right, wide open is obtuse, flat open is straight."
        },
        "angle-measure-facts": {
            simple: "Angles on a line sum to 180°; angles around a point sum to 360°.",
            probing: "If four angles on a straight line are equal, what is the measure of each?",
            misconception: "Forgetting to subtract from 360 when wrapping around a point.",
            analogy: "Cutting a pie — a half-pie cut always makes 180°, a whole pie cut is 360°."
        },
        "angle-algebra": {
            simple: "Translate spatial angle splits into algebraic sum equations.",
            probing: "What visual cue tells you to set the sum of these expressions to 180?",
            misconception: "Adding angles to 90 instead of 180 on a straight line.",
            analogy: "Combining jigsaw pieces to form a straight edge."
        }
    },

    /* ---- AI recommendations, keyed by slide id ---- */
    aiRecommendations: {
        "angle-types-basics": [
            { id: "rec-basics-1", text: "Ask students to point out acute angles in the classroom.", action: "annotate" },
            { id: "rec-basics-2", text: "Show 'Scissor Blades' real-life example.", kind: "show-example", target: "e1" }
        ],
        "angle-measure-facts": [
            { id: "rec-facts-1", text: "Try MCQ Q2 — adjacent angles on a line.", kind: "show-mcq", target: "m2" },
            { id: "rec-facts-2", text: "Show 'Clock Hands' real-life example.", kind: "show-example", target: "e2" }
        ],
        "angle-algebra": [
            { id: "rec-alg-1", text: "Solved example: Find value of y in (5y - 10)° and (3y + 30)°.", kind: "show-subjective", target: "s3" },
            { id: "rec-alg-2", text: "Start the drag-drop sorting activity.", kind: "start-activity" }
        ],
        "angle-recap": [
            { id: "rec-recap-1", text: "Quickly cycle through all 3 real-life examples.", action: "annotate" }
        ]
    }
};

export default angleLesson;
